import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import DailyOrder from './DailyOrder';
import MonthlyOrder from './MonthlyOrder';
import InstructionType from './InstructionType';
import UploadPriceType from './UploadPriceType';
import CardListInformation from './CardListInformation';
import AdditionalInformation from './AdditionalInformation';
import CustomStickyBar from '@src/components/CustomStickyBar';
import { Text, warningDialog, confirmDialog, useText } from '@src/components';

import {
  nextStep,
  setSelecteAutoUploadDetails,
  setDetailList,
  setResetList,
  setResetTempList,
  setActiveStep,
  setTotalAutoLoading,
  prepareAutoLoadingSlice,
  setAutoLoadingId,
  instructionDeleteTemp,
  setKeepIntegrationId,
  setSelectedRowKeys,
  setAutoLoadingNotIncludedCards,
  setIsAllSelectedRowKeys,
} from '@src/store/slice/autoUploadInstructionsSlice';
import { getAppSettings } from '@src/store/slice/appSettingsSlice';
import { getMaximumLoadedAmount } from '@src/store/slice/orderLimitsSlice';

import { Button } from 'antd';

import '@src/styles/cardOrder/cardInformation.scss';

const InstructionInformations = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [isPrepared, setIsPrepared] = useState(false);
  const [isExit, setIsExit] = useState(false);

  const { totalAutoLoading, activeStep, keepAutoLoadingId } = useSelector(
    (state) => state?.autoUploadInstructions,
  );

  const autoUploadInstructionsSelectActivationDateAndEnterAmount = useText(
    'autoUploadInstructions.selectActivationDateAndEnterAmount',
  );
  const autoUploadInstructionsEnterAmountForAtLeastOneCard = useText(
    'autoUploadInstructions.enterAmountForAtLeastOneCard',
  );

  const params = new URLSearchParams(history.location.search);
  let integrationId = params.get('updateId') ?? 0;
  if (!integrationId) {
    integrationId = params.get('instructionId') ?? 0;
  }

  const dialogMessage = (message) => {
    warningDialog({
      title: <Text t="warning2" />,
      message: message,
    });
  };

  useEffect(async () => {
    if (keepAutoLoadingId) {
      setIsPrepared(true);
      return;
    }
    const autoLanding = await dispatch(
      prepareAutoLoadingSlice({ integrationId: Number(integrationId) }),
    );
    await dispatch(setTotalAutoLoading({}));
    await dispatch(setAutoLoadingId(0));
    if (autoLanding?.payload?.data?.id) {
      await dispatch(setTotalAutoLoading(autoLanding.payload.data));
      await dispatch(setAutoLoadingId(autoLanding.payload.data.id));
    }
    await dispatch(setKeepIntegrationId(false));
    setIsPrepared(true);
  }, [keepAutoLoadingId]);

  const handleInstructionApproval = useCallback(async () => {
    if (!totalAutoLoading?.startDay && !totalAutoLoading?.totalPrice) {
      dialogMessage(autoUploadInstructionsSelectActivationDateAndEnterAmount);
    } else if (totalAutoLoading?.startDay === 0) {
      dialogMessage(
        'Lütfen Otomatik Yükleme Talimatınızın aktif olmasını istediğiniz tarihi seçiniz.',
      );
    } else if (!totalAutoLoading?.totalPrice) {
      dialogMessage(autoUploadInstructionsEnterAmountForAtLeastOneCard);
    } else {
      dispatch(nextStep());
      dispatch(setActiveStep(1));
    }
  }, [dispatch, totalAutoLoading]);

  const handleOrderCancel = async () => {
    await dispatch(setSelecteAutoUploadDetails([]));
    await dispatch(setDetailList([]));
    await dispatch(setResetList([]));
    await dispatch(setResetTempList([]));
    await dispatch(setIsAllSelectedRowKeys(false));
    await dispatch(setAutoLoadingNotIncludedCards([]));
    await dispatch(setSelectedRowKeys([]));
    await dispatch(
      setTotalAutoLoading({
        orderProcessType: 2,
        periodType: 1,
        startDay: 1,
        confirmEveryMonth: 0,
        totalCount: 0,
        totalPrice: 0,
        totalPaymentPrice: 0,
        vouId: 1,
        segId: 1000,
        monthlyPrice: 0.0,
        dailyPrice: 0.0,
        dayCount: 0,
        monthChoice: false,
      }),
    );
    history.push('/ticket-restaurant/auto-upload-instructions');
  };

  useEffect(() => {
    dispatch(getAppSettings({ code: 'MaxDailyUpload' }));
    dispatch(getAppSettings({ code: 'MaxDailyAmountWithoutTax' }));
    dispatch(getMaximumLoadedAmount());
  }, [dispatch]);

  useEffect(() => {
    const unblock = history.block((location) => {
      if (isExit) {
        return true;
      }

      if (
        activeStep === 0 &&
        location.pathname !== history.location.pathname &&
        totalAutoLoading?.id > 0
      ) {
        setIsExit(true);
        confirmDialog({
          title: <Text t="attention" />,
          htmlContent: <Text t="autoUploadBeforeUnloadMessage" />,
          okText: <Text t="yes" />,
          cancelText: <Text t="no" />,
          onOk: async (e) => {
            if (e?.target?.className?.split(' ')?.includes('close-image')) return;
            if (totalAutoLoading?.id > 0) {
              const body = { id: totalAutoLoading?.id };
              await dispatch(instructionDeleteTemp(body));
            }
            setIsExit(true);
            history.push(location.pathname);
          },
          onCancel: () => {
            setIsExit(false);
            return false;
          },
        });

        unblock();
      } else {
        return true;
      }

      return false;
    });

    return unblock;
  }, [activeStep, history, isExit, dispatch, totalAutoLoading]);

  if (!isPrepared) {
    return null;
  }
  return (
    <div className="balance-upload-content">
      <InstructionType />
      <UploadPriceType />
      <AdditionalInformation />

      {totalAutoLoading?.orderProcessType === 2 && (
        <>
          {totalAutoLoading?.periodType === 0 && <DailyOrder />}
          {totalAutoLoading?.periodType === 1 && <MonthlyOrder />}
        </>
      )}

      <CardListInformation />

      <CustomStickyBar>
        <div className="step-btn-container">
          <Button
            id="autoUploadInstructions-instructionInformations-cancel-btn"
            className="cancel-btn"
            onClick={handleOrderCancel}
          >
            <Text t="giveUp" />
          </Button>

          <Button
            id="autoUploadInstructions-instructionInformations-next-btn"
            className="next-btn"
            onClick={handleInstructionApproval}
          >
            <Text t="createInstruction" />
          </Button>
        </div>
      </CustomStickyBar>
    </div>
  );
};

export default InstructionInformations;
